import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { EtapaIcon } from "@/components/rastreo/EtapaIcon";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function EmbarqueDetalle() {
  const { booking } = useParams();
  usePageTitle(`Embarque ${booking}`);
  const queryClient = useQueryClient();
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  const [confirmarAvanzar, setConfirmarAvanzar] = useState(false);
  const [resultado, setResultado] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-embarque", booking],
    queryFn: () => adminApi.get(`/admin/embarques/${encodeURIComponent(booking)}`).then((r) => r.data),
  });

  useEffect(() => {
    if (data) setSeleccionadas(new Set(data.unidades.map((u) => u.id)));
  }, [data]);

  const avanzarMutation = useMutation({
    mutationFn: () =>
      adminApi.post("/admin/embarques/avanzar", {
        booking, unidades_ids: Array.from(seleccionadas),
      }).then((r) => r.data),
    onSuccess: (resp) => {
      setResultado(resp);
      queryClient.invalidateQueries({ queryKey: ["admin-embarque", booking] });
      queryClient.invalidateQueries({ queryKey: ["admin-embarques"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unidades"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmarAvanzar(false),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <Loader2 className="animate-spin text-[var(--tc-accent)]" size={26} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <ErrorState testId="embarque-detalle-error" mensaje="No se pudo cargar este embarque." onRetry={refetch} />
      </div>
    );
  }

  const { cliente, pedido_cliente, total_unidades, unidades } = data;

  const toggleTodas = () => {
    setSeleccionadas((s) => (s.size === unidades.length ? new Set() : new Set(unidades.map((u) => u.id))));
  };
  const toggleUna = (id) => {
    setSeleccionadas((s) => {
      const copia = new Set(s);
      if (copia.has(id)) copia.delete(id); else copia.add(id);
      return copia;
    });
  };

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/admin/embarques" className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:text-[var(--tc-accent)]">
        <ArrowLeft size={15} /> Volver a embarques
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
        <div>
          <h1 className="font-mono text-lg font-semibold text-[var(--tc-text)]">{booking}</h1>
          <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
            {cliente?.nombre || "—"} · {total_unidades} unidad{total_unidades === 1 ? "" : "es"}
            {pedido_cliente && <> · Pedido: {pedido_cliente}</>}
          </p>
        </div>
        <button
          data-testid={ADMIN.embarqueDetalleAdvanceButton}
          onClick={() => setConfirmarAvanzar(true)}
          disabled={seleccionadas.size === 0}
          className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-50"
        >
          Avanzar todo el embarque <ChevronRight size={16} />
        </button>
      </div>

      <div className="mt-5 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        <table className="w-full min-w-[640px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
          <thead>
            <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
              <th className="px-4 py-3">
                <input
                  data-testid={ADMIN.embarqueDetalleSelectAllCheckbox}
                  type="checkbox"
                  checked={seleccionadas.size === unidades.length && unidades.length > 0}
                  onChange={toggleTodas}
                  className="h-4 w-4 accent-[var(--tc-accent)]"
                />
              </th>
              <th className="px-4 py-3">VIN</th>
              <th className="px-4 py-3">Marca / Modelo</th>
              <th className="px-4 py-3">Etapa actual</th>
            </tr>
          </thead>
          <tbody>
            {unidades.map((u) => (
              <tr key={u.id} data-testid={ADMIN.embarqueDetalleRow} className="border-b border-[var(--tc-border)] last:border-0">
                <td className="px-4 py-3">
                  <input
                    data-testid={ADMIN.embarqueDetalleRowCheckbox}
                    type="checkbox"
                    checked={seleccionadas.has(u.id)}
                    onChange={() => toggleUna(u.id)}
                    className="h-4 w-4 accent-[var(--tc-accent)]"
                  />
                </td>
                <td className="px-4 py-3 font-mono text-xs tabular-nums text-[var(--tc-text-secondary)]">
                  {u.vin.slice(0, -6)}<strong className="text-[var(--tc-text)]">{u.vin_last6}</strong>
                </td>
                <td className="px-4 py-3 font-medium text-[var(--tc-text)]">{u.marca} {u.modelo}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tc-accent-soft)] px-2.5 py-1 text-xs font-medium text-[var(--tc-accent)]">
                    <EtapaIcon nombre={u.etapa_actual?.icono} size={13} /> {u.etapa_actual?.nombre || "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={confirmarAvanzar} onOpenChange={setConfirmarAvanzar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Avanzar {seleccionadas.size} unidad{seleccionadas.size === 1 ? "" : "es"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Cada unidad avanzará una etapa desde donde esté. Las que ya estén en la última etapa se van a omitir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={ADMIN.confirmDialogConfirm} onClick={() => avanzarMutation.mutate()}>Avanzar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(resultado)} onOpenChange={(v) => !v && setResultado(null)}>
        <DialogContent data-testid={ADMIN.embarqueDetalleResultDialog}>
          <DialogHeader>
            <DialogTitle>Resultado del avance</DialogTitle>
            <DialogDescription>
              {resultado?.avanzadas} unidad{resultado?.avanzadas === 1 ? "" : "es"} avanzada{resultado?.avanzadas === 1 ? "" : "s"}, {resultado?.omitidas} omitida{resultado?.omitidas === 1 ? "" : "s"}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {resultado?.detalle_avanzadas?.length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--tc-text-secondary)]">
                  <CheckCircle2 size={13} className="text-[var(--tc-accent)]" /> Avanzadas
                </p>
                <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-[var(--tc-text)]">
                  {resultado.detalle_avanzadas.map((a, i) => (
                    <li key={i}>...{a.vin.slice(-6)} → {a.etapa}</li>
                  ))}
                </ul>
              </div>
            )}
            {resultado?.detalle_omitidas?.length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--tc-text-secondary)]">
                  <XCircle size={13} className="text-[var(--tc-state-pending)]" /> Omitidas
                </p>
                <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-[var(--tc-text-secondary)]">
                  {resultado.detalle_omitidas.map((o, i) => (
                    <li key={i}>...{o.vin.slice(-6)} — {o.motivo}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
